// Custom replicas and props from a photo. Claims stay inside .seo/truth.md:
// 3D modeling starts at $250 (public 3D-modeling price), large pieces are
// printed in sections and assembled, prices and timing are quoted per project.
// Buyer questions: AI-visibility prompts #14–17 (2026-10-29) and real
// inquiries (a display replica from one reference image, a 2D photo turned
// into a bronze-finish relief).
const replicaPricing = { modelingFrom: 250 }

const replica = {
  path: '/services/3d-print-from-photo/',
  reviewedDate: '2026-11-01',
  reviewedDateLabel: 'November 1, 2026',
  title: '3D Print From a Photo: Custom Props & Statues | PrintAWorld',
  description: `We 3D print from your photos: custom props, replicas, statues and giant props, no 3D file needed. Made and painted in New York; modeling from $${replicaPricing.modelingFrom}.`,
  answer: `PrintAWorld makes 3D prints from photos in New York: custom props, replicas and statues, no 3D file needed. Send a few pictures, or even one reference image; we model it in 3D, print it at the size you need, and paint it to match. 3D modeling starts at $${replicaPricing.modelingFrom}, and we ship anywhere in the U.S.`,
  // Hero "before" image: an AI-generated stand-in for the photo a client
  // sends, made from our real gold-plated cleats. Swap for a real client
  // photo once the owner supplies one.
  heroPhoto: {
    src: '/images/3d-print-from-photo/original-cleats-1200.webp',
    srcSet:
      '/images/3d-print-from-photo/original-cleats-800.webp 800w, /images/3d-print-from-photo/original-cleats-1200.webp 1200w',
    alt: 'AI-generated example photo of a worn pair of black leather soccer cleats on a wooden floor',
  },
}

// Contentful files already used on the trophy, scanning and figure pages.
const photos = {
  cleats: {
    file: '2TEJynhwC56Gd9DQDzZrcU/31b3cab828bdba6957dee3d93e10877c/LAL_6816.jpg',
    alt: 'A pair of gold-plated soccer cleats',
  },
  relief: {
    file: '5hKi0n2cf5vDuNSW9tVmUZ/b00767d4496950c7e713050efde7004d/_DSC3266.jpg',
    alt: 'A bronze plaque with a relief portrait and engraved text',
  },
  angel: {
    file: '3LTdnR8ERy8Y04wiu2Acm0/4521bf0b61f09690ec82812d48522d44/3D_Scanned_angel_sculpture.jpg',
    alt: 'Angel sculpture captured through 3D scanning',
  },
  creature: {
    file: '1wszBnKk9nXFw8ThSLFTlw/f2959211749317d873982d2b743c7e0a/LAL_4464.jpg',
    alt: 'Glossy red creature sculpture with claws on a white base against a black background',
  },
}

const uses = [
  {
    title: 'Display replicas',
    body: 'A copy of a product, appliance or artifact for a store window, a museum case or a launch table. It looks like the real thing from a few feet away.',
  },
  {
    title: 'Custom props for shoots and events',
    body: 'We’re the prop maker for lightweight stand-ins that read on camera: hero props, background pieces, things too fragile or too valuable to bring to set.',
  },
  {
    title: 'Giant props and oversized products',
    body: 'A giant can, bottle or sneaker for an event or a brand activation. Big pieces are printed in sections, joined and finished as one.',
    link: ['Custom props and giant replicas', '/services/custom-props/'],
  },
  {
    title: 'Custom statues from a photo',
    body: 'A person, a pet or a logo turned into a custom statue or sculpture, or a flat photo turned into a raised relief with a bronze or painted finish.',
  },
  {
    title: 'Copies of something you own',
    body: 'If you have the object, we can 3D scan it instead of working from photos, then print copies at any scale.',
    link: ['About 3D scanning', '/services/3d-scanning/'],
  },
  {
    title: 'From an AI image',
    body: 'An AI-generated picture of a prop that doesn’t exist yet works as a reference too. We model it, then send you the 3D model to approve.',
  },
]

const photoTips = [
  'Shoot the front, both sides, the back and the top, on a plain background.',
  'Add one real measurement, like the height, or put a ruler in a photo.',
  'Take close-ups of logos, labels and textures you want copied.',
  'Only have one image? Send it. We’ll fill in the hidden sides and show you the model before printing.',
]

const finishes = [
  {
    title: 'Display finish',
    body: 'Printed, sanded smooth, primed and painted to match the photos. This is the version that holds up in a window or on camera.',
  },
  {
    title: 'Budget print',
    body: 'Printed in one color with visible layer lines and no painting. It costs much less than the display finish and works for mockups, fit checks and background props.',
  },
]

const faqs = [
  {
    question: 'Can you 3D print something from a photo?',
    answer:
      'Yes. Several photos from different sides work best, but one clear reference image is enough to start. We model the object in 3D from the photos and send you the model to approve before anything is printed.',
  },
  {
    question: 'How much does a custom replica or prop cost?',
    answer: `3D modeling starts at $${replicaPricing.modelingFrom}. Printing and finishing are quoted for each project, because size, detail and paint are what decide the price. A one-color budget print costs much less than a fully painted display replica, so tell us how the piece will be used.`,
  },
  {
    question: 'Will the replica work like the real thing?',
    answer:
      'No. Replicas are made to look right, not to function: no moving parts, electronics or heat, and printed parts are not food-safe. If it needs to hold food, we can design it around a food-safe container that sits inside.',
  },
  {
    question: 'How big can you make it?',
    answer:
      'From desk-sized pieces to giant props. Single prints go up to about 6 feet, and larger pieces are printed in sections, assembled and finished as one.',
  },
  {
    question: 'Can you copy a branded product or a character?',
    answer:
      'If the brand or character belongs to someone else, you need their permission to reproduce it. Brands and agencies often send us their own products for launches, displays and shoots.',
  },
  {
    question: 'Do you ship?',
    answer:
      'Yes, anywhere in the United States. Pickup from our New York studios is available by appointment.',
  },
]

module.exports = {
  replica,
  replicaPricing,
  photos,
  uses,
  photoTips,
  finishes,
  faqs,
}
